'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import type { Session } from '@supabase/supabase-js';
import Sidebar from '@/components/layout/sidebar';
import SidebarMini from '@/components/layout/sidebar-mini';
import BottomNav from '@/components/layout/bottom-nav';
import Topbar from '@/components/layout/topbar';
import SiteFooter from '@/components/layout/site-footer';
import NotificationDrawer from '@/components/notifications/notification-drawer';
import { cn } from '@/lib/utils';
import { isPublicContentPath, languageCodeFromPathname, localizedPublicPath, normalizeLanguageCode } from '@/i18n/config';
import { useLangDataStore, usePuzzleListStore, useThemeStore, useUserStore } from '@/utils/util_states';
import { getSession, onAuthStateChange, queryRow } from '@/action/supabase';
import type { UserProfile } from '@/utils/util_types';
import { loadUserPuzzleChats, splitPuzzleChats } from '@/services/puzzle-chat';

type MainShellProps = {
  children: React.ReactNode;
  initialLanguageCode: string;
};

export default function MainShell({ children, initialLanguageCode }: MainShellProps) {
  const pathname = usePathname();
  const router = useRouter();
  const pathLanguageCode = languageCodeFromPathname(pathname);
  const { initLanguageFromSystem, languageCode, langReady } = useLangDataStore();
  const { initTheme } = useThemeStore();
  const { userProfile, isLogin, authReady, updateUser, updateIsLogin, updateAuthReady, resetUser } = useUserStore();
  const { setPuzzleChats, clearPuzzleChats } = usePuzzleListStore();
  const sessionUserIdRef = useRef<string | null>(null);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const closeNotificationDrawer = useCallback(() => setNotificationDrawerOpen(false), []);
  const effectiveLanguageCode = langReady ? languageCode : normalizeLanguageCode(initialLanguageCode);
  const isLocalizedPublicPath = Boolean(pathLanguageCode && isPublicContentPath(pathname));
  const isChatPage = pathname.startsWith('/chat/');

  useEffect(() => {
    void initLanguageFromSystem(pathLanguageCode ?? initialLanguageCode);
  }, [initLanguageFromSystem, initialLanguageCode, pathLanguageCode]);

  useEffect(() => {
    if (!langReady || !isLocalizedPublicPath || !pathLanguageCode || pathLanguageCode === languageCode) return;
    router.replace(localizedPublicPath(pathname, languageCode));
  }, [isLocalizedPublicPath, langReady, languageCode, pathLanguageCode, pathname, router]);

  useEffect(() => {
    void initTheme();
  }, [initTheme]);

  useEffect(() => {
    let disposed = false;
    let latestSync = 0;

    const applySession = async (session: Session | null) => {
      const userId = session?.user.id ?? null;
      const sessionEmail = session?.user.email;

      // Supabase refreshes tokens when a background tab becomes active again.
      // The user and profile have not changed, so keep the app ready and avoid
      // refetching every page-level data source.
      if (userId && sessionUserIdRef.current === userId) {
        return;
      }

      const syncId = ++latestSync;
      sessionUserIdRef.current = userId;
      if (!disposed) updateAuthReady(false);

      try {
        if (!userId) {
          if (!disposed && syncId === latestSync) {
            resetUser();
            updateAuthReady(true);
          }
          return;
        }

        const row = await queryRow('profiles', 'id', userId);
        if (row?.status === 'wait_removal') {
          if (!disposed && syncId === latestSync) {
            resetUser();
            window.location.replace('/login');
          }
          return;
        }
        const profile: UserProfile = {
          id: userId,
          username: row?.username ?? sessionEmail?.split('@')[0] ?? '',
          avatar: row?.avatar ?? null,
          coins: Number(row?.coins ?? 0),
          solvePuzzleNum: Number(row?.solvePuzzleNum ?? 0),
          likePuzzles: Array.isArray(row?.likePuzzles) ? row.likePuzzles : [],
          puzzleChats: Array.isArray(row?.puzzleChats) ? row.puzzleChats : [],
          is_subscribed: Boolean(row?.is_subscribed),
          subscription_source: typeof row?.subscription_source === 'string' ? row.subscription_source : null,
          subscription_status: typeof row?.subscription_status === 'string' ? row.subscription_status : null,
          free_ai_create: Boolean(row?.free_ai_create),
          status: typeof row?.status === 'string' ? row.status : 'normal',
        };

        if (!disposed && syncId === latestSync) {
          updateUser(profile);
          updateIsLogin(true);
        }
      } catch (error) {
        console.error('Session restore failed:', error);
        if (!disposed && syncId === latestSync) {
          sessionUserIdRef.current = null;
          resetUser();
        }
      } finally {
        if (!disposed && syncId === latestSync) updateAuthReady(true);
      }
    };

    const subscription = onAuthStateChange((_event, session) => {
      void applySession(session);
    });

    void getSession()
      .then(({ session }) => applySession(session))
      .catch((error) => {
        console.error('Session lookup failed:', error);
        if (!disposed) {
          resetUser();
          updateAuthReady(true);
        }
      });

    return () => {
      disposed = true;
      subscription.unsubscribe();
    };
  }, [resetUser, updateAuthReady, updateIsLogin, updateUser]);

  useEffect(() => {
    if (!authReady) return;
    if (!isLogin || !userProfile.id) {
      clearPuzzleChats();
      return;
    }

    let cancelled = false;
    void loadUserPuzzleChats(languageCode)
      .then((chats) => {
        if (cancelled) return;
        const { active, completed } = splitPuzzleChats(chats);
        setPuzzleChats(active, completed);
      })
      .catch((error) => {
        if (!cancelled) console.error('Case list restore failed:', error);
      });
    return () => { cancelled = true; };
  }, [authReady, clearPuzzleChats, isLogin, languageCode, setPuzzleChats, userProfile.id]);

  return (
    <div className="flex min-h-screen">
      <div className="hidden sm:block lg:hidden">
        <SidebarMini languageCode={effectiveLanguageCode} />
      </div>
      <div className="hidden lg:block">
        <Sidebar languageCode={effectiveLanguageCode} />
      </div>

      <div className={cn(
        'mobile-shell-content-offset flex min-w-0 flex-1 flex-col sm:ml-[68px]',
        'lg:ml-[208px]',
        !isChatPage && 'mobile-bottom-nav-spacer lg:pb-0',
      )}>
        <Topbar
          languageCode={effectiveLanguageCode}
          notificationDrawerOpen={notificationDrawerOpen}
          onOpenNotifications={() => setNotificationDrawerOpen(true)}
        />
        <main className="flex-1">{children}</main>
        <SiteFooter languageCode={effectiveLanguageCode} />
      </div>

      <BottomNav languageCode={effectiveLanguageCode} />
      <NotificationDrawer open={notificationDrawerOpen} onClose={closeNotificationDrawer} />
    </div>
  );
}
